import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';

// --- DATOS DEL CUESTIONARIO ---
const questions = [
  { cat: "Identidad visual", text: "¿Cuándo se creó o renovó por última vez tu logo?", area: "visual",
    opts: [
      { label: "Hace menos de 2 años", score: 10 },
      { label: "Entre 2 y 5 años", score: 7 },
      { label: "Hace más de 5 años", score: 3 },
      { label: "No tengo logo definido o lo hizo alguien sin ser diseñador", score: 0 }
    ]
  },
  { cat: "Identidad visual", text: "¿Tienes colores, tipografías y estilos definidos que uses de forma consistente?", area: "visual",
    opts: [
      { label: "Sí, tenemos un manual de marca y lo aplicamos", score: 10 },
      { label: "Tenemos algo definido pero no siempre lo respetamos", score: 6 },
      { label: "Usamos colores 'de ojo' sin documentar", score: 2 },
      { label: "No hay nada definido", score: 0 }
    ]
  },
  { cat: "Presencia digital", text: "¿Cómo describirías tu página web?", area: "digital",
    opts: [
      { label: "Moderna, rápida y optimizada para móvil", score: 10 },
      { label: "Funcional pero con un diseño algo desfasado", score: 5 },
      { label: "Básica, hecha con una plantilla genérica", score: 2 },
      { label: "No tengo web o está desactualizada", score: 0 }
    ]
  },
  { cat: "Presencia digital", text: "¿Tus perfiles en redes sociales tienen un look coherente entre sí y con tu web?", area: "digital",
    opts: [
      { label: "Sí, todo tiene el mismo estilo y se reconoce", score: 10 },
      { label: "Más o menos, aunque hay inconsistencias", score: 5 },
      { label: "Cada red tiene un estilo diferente", score: 2 },
      { label: "No tenemos presencia activa en redes", score: 0 }
    ]
  },
  { cat: "Comunicación", text: "¿Tienes definido cómo habla tu marca? (tono, lenguaje, mensajes clave)", area: "comunicacion",
    opts: [
      { label: "Sí, tenemos guía de tono y voz de marca", score: 10 },
      { label: "Hay algo definido pero informal", score: 6 },
      { label: "Cada persona comunica a su manera", score: 2 },
      { label: "No está definido", score: 0 }
    ]
  },
  { cat: "Comunicación", text: "Cuando alguien ve tu marca por primera vez, ¿entiende enseguida qué haces y para quién?", area: "comunicacion",
    opts: [
      { label: "Sí, es claro y diferenciador", score: 10 },
      { label: "Más o menos, pero habría que explicarlo", score: 5 },
      { label: "Suele generar confusión", score: 2 },
      { label: "No tengo claro cómo comunicarlo yo mismo", score: 0 }
    ]
  },
  { cat: "Diferenciación", text: "¿En qué se diferencia tu negocio de la competencia directa?", area: "diferenciacion",
    opts: [
      { label: "Tenemos un posicionamiento claro y único", score: 10 },
      { label: "Hay diferencias pero no las comunicamos bien", score: 6 },
      { label: "Hacemos lo mismo que los demás pero más barato", score: 2 },
      { label: "No lo tengo claro", score: 0 }
    ]
  },
  { cat: "Diferenciación", text: "¿Tu imagen transmite el nivel de precio o calidad que ofreces?", area: "diferenciacion",
    opts: [
      { label: "Sí, la imagen está alineada con el precio", score: 10 },
      { label: "Creo que nos hace parecer más baratos de lo que somos", score: 4 },
      { label: "La imagen es mejor que el servicio real", score: 5 },
      { label: "No he pensado en eso", score: 1 }
    ]
  },
  { cat: "Materiales", text: "¿Qué materiales de presentación tienes para clientes o proveedores?", area: "materiales",
    opts: [
      { label: "Dossier, presentaciones, tarjetas… todo coherente", score: 10 },
      { label: "Algo, pero no está muy trabajado", score: 5 },
      { label: "Solo tarjetas o nada", score: 2 },
      { label: "Mando el presupuesto en Word o PDF básico", score: 0 }
    ]
  },
  { cat: "Materiales", text: "¿Con qué frecuencia recibes comentarios positivos sobre la imagen de tu marca?", area: "materiales",
    opts: [
      { label: "Con frecuencia, la imagen genera confianza", score: 10 },
      { label: "Alguna vez, pero no es algo que destaquen", score: 6 },
      { label: "Rara vez", score: 2 },
      { label: "Nunca o me han pedido que lo mejore", score: 0 }
    ]
  }
];

const areaNames = { visual: "Identidad visual", digital: "Presencia digital", comunicacion: "Comunicación", diferenciacion: "Diferenciación", materiales: "Materiales" };
const areaColors = { visual: "#e14141", digital: "#3b82f6", comunicacion: "#f59e0b", diferenciacion: "#8b5cf6", materiales: "#10b981" };

const getInsight = (area, pct) => {
  const map = {
    visual: pct < 40 ? "Tu identidad visual genera desconfianza antes de que te conozcan. Un restyling cambiará la percepción de inmediato." : "Tienes base visual pero hay inconsistencias. Un pequeño trabajo de estandarización tiene mucho impacto.",
    digital: pct < 40 ? "Tu presencia online no refleja tu calidad real. Si la web no convence en 3 segundos, el cliente se va a la competencia." : "Tu apartado digital funciona pero no convierte todo lo que podría. Coherencia y UX son la clave ahora.",
    comunicacion: pct < 40 ? "Sin un mensaje claro, dependes del boca a boca. Definir tu voz de marca te permite escalar y vender en automático." : "Tu comunicación es buena pero le falta consistencia. Un tono de voz definido genera confianza ciega.",
    diferenciacion: pct < 40 ? "Si no comunicas en qué eres diferente, el cliente decide solo por precio (y ahí siempre se pierde)." : "Te diferencias en la práctica pero no en la percepción. Toca hacer visible lo que ya te distingue del resto.",
    materiales: pct < 40 ? "Los materiales son tu comercial silencioso. Si no están a la altura, pierdes credibilidad al dar el presupuesto." : "Tienes materiales pero no están optimizados para cerrar ventas. Un dossier premium puede duplicar tu ratio de cierre."
  };
  return map[area] || "";
};

export default function BrandAudit() {
  const [screen, setScreen] = useState('hero'); 
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState(new Array(questions.length).fill(null));
  const [formData, setFormData] = useState({ name: '', company: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultsData, setResultsData] = useState(null);

  // NUEVOS ESTADOS PARA EL BOTÓN DE LLAMADA
  const [isAgendando, setIsAgendando] = useState(false);
  const [agendadoSuccess, setAgendadoSuccess] = useState(false);

  useEffect(() => {
    // Truco definitivo para navegadores de escritorio: desactivar la "memoria" de scroll
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    window.scrollTo(0, 0);
  }, [screen]);

  const handleOptionSelect = (idx, score) => {
    const newAnswers = [...answers];
    newAnswers[currentQ] = { idx, score };
    setAnswers(newAnswers);
  };

  const calculateResults = () => {
    const total = answers.reduce((s, a) => s + (a ? a.score : 0), 0);
    const pct = Math.round((total / (questions.length * 10)) * 100);

    const areaScores = {};
    const areaCounts = {};
    questions.forEach((q, i) => {
      if (!areaScores[q.area]) { areaScores[q.area] = 0; areaCounts[q.area] = 0; }
      areaScores[q.area] += answers[i] ? answers[i].score : 0;
      areaCounts[q.area]++;
    });

    let badge, badgeColor, desc, ctaTitle, ctaBody;
    if (pct >= 75) {
      badge = "Marca Sólida"; badgeColor = "text-emerald-400 border-emerald-400/30 bg-emerald-400/10";
      desc = "Tu identidad tiene muy buenas bases. El reto ahora es afinar detalles premium y escalarla para liderar tu sector.";
      ctaTitle = "Pule los últimos detalles"; 
      ctaBody = "Hay margen para llevar tu marca al nivel top. Nuestro equipo se pondrá en contacto contigo en cuanto revisen tu situación. También puedes solicitar una llamada de asesoramiento tú mismo en el siguiente botón.";
    } else if (pct >= 50) {
      badge = "Marca en Construcción"; badgeColor = "text-orange-400 border-orange-400/30 bg-orange-400/10";
      desc = "Tienes elementos buenos pero la inconsistencia está costándote cierres de ventas. Con un trabajo puntual la percepción cambiará radicalmente.";
      ctaTitle = "Este es el momento exacto"; 
      ctaBody = "Las marcas en tu nivel que unifican su identidad consiguen subir precios y captar mejores clientes. Nuestro equipo se pondrá en contacto contigo en cuanto revisen tu situación. También puedes solicitar una llamada de asesoramiento tú mismo en el siguiente botón.";
    } else {
      badge = "Marca Frágil — Oportunidad Real"; badgeColor = "text-[#e14141] border-[#e14141]/30 bg-[#e14141]/10";
      desc = "La imagen actual está limitando seriamente tu crecimiento. La buena noticia: el impacto de mejorarla será inmediato y muy rentable.";
      ctaTitle = "Tu imagen te está costando dinero"; 
      ctaBody = "Con las bases correctas, los negocios en tu situación consiguen justificar precios más altos y atraer al cliente ideal que antes los rechazaba. Nuestro equipo se pondrá en contacto contigo en cuanto revisen tu situación. También puedes solicitar una llamada de asesoramiento tú mismo en el siguiente botón.";
    }

    const breakdown = Object.keys(areaScores).map(area => ({
      area,
      name: areaNames[area],
      color: areaColors[area],
      pct: Math.round((areaScores[area] / (areaCounts[area] * 10)) * 100)
    }));

    const weakAreas = [...breakdown].sort((a, b) => a.pct - b.pct).slice(0, 2);

    return { pct, badge, badgeColor, desc, ctaTitle, ctaBody, breakdown, weakAreas };
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  const results = calculateResults();
  setResultsData(results);

  try {
    await fetch('https://first-studio-backend.onrender.com/api/auditoria-gratuita', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        Nombre: formData.name,
        Empresa: formData.company,
        Email: formData.email,
        Puntuacion_Global: results.pct + '%',
        Respuestas_Area: results.breakdown.map(b => `${b.name}: ${b.pct}%`).join(' | ')
      })
    });
  } catch (error) {
    console.error('Error:', error);
  } finally {
    setIsSubmitting(false);
    setScreen('result');
  }
};

  // NUEVA FUNCIÓN PARA EL BOTÓN DE AGENDAR LLAMADA
  const handleAgendarLlamada = async () => {
  setIsAgendando(true);
  try {
    await fetch('https://first-studio-backend.onrender.com/api/auditoria-gratuita', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        Nombre: formData.name,
        Empresa: formData.company,
        Email: formData.email,
        Puntuacion_Global: resultsData.pct + '%',
        Respuestas_Area: resultsData.breakdown.map(b => `${b.name}: ${b.pct}%`).join(' | '),
        URGENTE: "⚠️ EL CLIENTE HA SOLICITADO UNA LLAMADA DE ASESORAMIENTO AHORA"
      })
    });
  } catch (error) {
    console.error('Error:', error);
  } finally {
    // ✅ Espera a que el servidor responda antes de mostrar éxito
    setIsAgendando(false);
    setAgendadoSuccess(true);
  }
};

  return (
    <div className="w-full flex-grow text-zinc-100 font-sans flex flex-col relative overflow-hidden bg-transparent">
      
      {/* Background Glows Optimizados: Cambiamos el 'blur' pesado por un degradado radial CSS puro */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-[radial-gradient(ellipse_at_top,_rgba(225,65,65,0.15)_0%,_transparent_70%)] pointer-events-none" />

      {/* MAIN CONTENT AREA */}
      <main className="flex-grow flex flex-col items-center justify-center py-24 md:py-32 px-4 md:px-6 relative z-10 w-full max-w-5xl mx-auto min-h-screen">
        <AnimatePresence mode="wait">
          
          {/* --- PANTALLA 1: HERO --- */}
          {screen === 'hero' && (
            <motion.div 
              key="hero" 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }} 
              className="w-full max-w-3xl flex flex-col items-center justify-center text-center mt-12 md:mt-24"
            >
              {/* Aplicamos font-heading para el título */}
              <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight mb-8 leading-[1.1] text-white">
                Conoce el estado <br className="hidden sm:block"/>
                <span className="text-white">de tu marca.</span>
              </h1>
              
              <p className="font-body text-zinc-400 text-base sm:text-lg md:text-xl mb-12 leading-relaxed font-medium px-4">
                10 preguntas. 3 minutos. Un diagnóstico honesto y profesional de la identidad de tu negocio, sin coste y sin compromiso.
              </p>
              
              <button 
                onClick={() => setScreen('quiz')}
                className="font-body group relative inline-flex items-center justify-center gap-3 bg-[#e14141] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-red-600 transition-all duration-300 active:scale-95"
              >
                Comenzar el diagnóstico
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          )}

          {/* --- PANTALLA 2: CUESTIONARIO --- */}
          {screen === 'quiz' && (
            <motion.div 
              key="quiz" 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              exit={{ opacity: 0, x: -20, transition: { duration: 0.2 } }} 
              className="w-full max-w-2xl mt-8"
            >
              
              <div className="flex items-center justify-between mb-6">
                <span className="font-body text-xs sm:text-sm font-bold text-[#e14141] uppercase tracking-wider">{questions[currentQ].cat}</span>
                <span className="font-body text-xs sm:text-sm font-semibold text-zinc-400 bg-zinc-900/80 px-3 py-1 rounded-full border border-zinc-800">
                  {currentQ + 1} / {questions.length}
                </span>
              </div>

              <div className="w-full h-1.5 bg-zinc-900 rounded-full mb-8 sm:mb-10 overflow-hidden border border-zinc-800/50">
                <motion.div 
                  className="h-full bg-[#e14141]"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentQ + 1) / questions.length) * 100}%` }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                />
              </div>

              <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-10 backdrop-blur-md mb-8">
                {/* Aplicamos font-heading a la pregunta */}
                <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-8 leading-snug text-white">
                  {questions[currentQ].text}
                </h2>
                
                <div className="flex flex-col gap-3">
                  {questions[currentQ].opts.map((opt, i) => {
                    const isSelected = answers[currentQ]?.idx === i;
                    return (
                      <button
                        key={i}
                        onClick={() => handleOptionSelect(i, opt.score)}
                        className={`font-body group flex items-start sm:items-center gap-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl border text-left transition-colors duration-200 ${
                          isSelected 
                          ? 'border-[#e14141] bg-[#e14141]/10 text-white' 
                          : 'border-zinc-800/80 bg-zinc-950/50 text-zinc-400 hover:border-zinc-600 hover:bg-zinc-900 hover:text-zinc-200'
                        }`}
                      >
                        <div className={`w-5 h-5 mt-0.5 sm:mt-0 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'border-[#e14141]' : 'border-zinc-600'}`}>
                          {isSelected && <div className="w-2.5 h-2.5 bg-[#e14141] rounded-full" />}
                        </div>
                        <span className="text-sm sm:text-base md:text-lg font-medium leading-tight">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button 
                  onClick={() => currentQ > 0 ? setCurrentQ(currentQ - 1) : setScreen('hero')}
                  className="font-body flex items-center gap-2 text-zinc-400 hover:text-white py-2 transition-colors font-medium text-sm sm:text-base"
                >
                  <ArrowLeft size={18} /> Atrás
                </button>
                
                <button 
                  disabled={answers[currentQ] === null}
                  onClick={() => currentQ < questions.length - 1 ? setCurrentQ(currentQ + 1) : setScreen('contact')}
                  className="font-body flex items-center gap-2 bg-white text-black px-6 sm:px-8 py-3 rounded-full font-bold hover:bg-zinc-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95 text-sm sm:text-base"
                >
                  {currentQ === questions.length - 1 ? 'Ver resultados' : 'Siguiente'} <ArrowRight size={18} />
                </button>
              </div>
            </motion.div>
          )}

          {/* --- PANTALLA 3: FORMULARIO CONTACTO --- */}
          {screen === 'contact' && (
            <motion.div 
              key="contact" 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
              className="w-full max-w-lg mt-8"
            >
              
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#e14141]/10 text-[#e14141] mb-6 border border-[#e14141]/20">
                  <Check size={28} strokeWidth={2.5} />
                </div>
                {/* Aplicamos font-heading al título */}
                <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3 tracking-tight text-white">Análisis completado</h2>
                <p className="font-body text-zinc-400 text-base sm:text-lg font-medium">¿A dónde enviamos tus resultados?</p>
              </div>

              <form onSubmit={handleSubmit} className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl sm:rounded-3xl p-6 sm:p-8 backdrop-blur-md">
                <div className="space-y-4 sm:space-y-5">
                  <div>
                    <label className="font-body block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 ml-1">Nombre</label>
                    <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="font-body w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 sm:px-5 py-3 sm:py-4 text-white placeholder-zinc-600 focus:outline-none focus:border-[#e14141] transition-colors font-medium text-sm sm:text-base" placeholder="Tu nombre" />
                  </div>
                  <div>
                    <label className="font-body block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 ml-1">Empresa / Proyecto</label>
                    <input required type="text" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} className="font-body w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 sm:px-5 py-3 sm:py-4 text-white placeholder-zinc-600 focus:outline-none focus:border-[#e14141] transition-colors font-medium text-sm sm:text-base" placeholder="Nombre de tu marca" />
                  </div>
                  <div>
                    <label className="font-body block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 ml-1">Correo electrónico</label>
                    <input required type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="font-body w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 sm:px-5 py-3 sm:py-4 text-white placeholder-zinc-600 focus:outline-none focus:border-[#e14141] transition-colors font-medium text-sm sm:text-base" placeholder="hola@tuempresa.com" />
                  </div>
                </div>

                {/* NUEVO: Checkbox de consentimiento */}
                <div className="mt-6 flex items-start gap-3">
                  <div className="flex items-center h-5">
                    <input
                      id="consent"
                      type="checkbox"
                      required
                      checked={formData.consent}
                      onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                      className="w-4 h-4 border-zinc-800 rounded bg-zinc-900 accent-[#e14141] focus:ring-[#e14141] focus:ring-1 focus:ring-offset-zinc-950 focus:ring-offset-2 cursor-pointer"
                    />
                  </div>
                  <label htmlFor="consent" className="font-body text-xs text-zinc-400 leading-tight cursor-pointer">
                    He leído y acepto la <a href="/politica-privacidad" target="_blank" rel="noopener noreferrer" className="text-[#e14141] hover:underline">Política de Privacidad</a> y consiento el tratamiento de mis datos para recibir los resultados y comunicaciones sobre mis servicios.
                  </label>
                </div>
                
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="font-body w-full mt-6 sm:mt-8 bg-[#e14141] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-wait"
                >
                  {isSubmitting ? (
                    <span className="animate-pulse">Analizando matriz de datos...</span>
                  ) : (
                    <>Revelar mis resultados <Sparkles size={18} /></>
                  )}
                </button>
                <p className="font-body text-center text-xs font-medium text-zinc-500 mt-4">Tus datos están seguros. 100% libre de spam.</p>
              </form>
            </motion.div>
          )}

          {/* --- PANTALLA 4: RESULTADOS --- */}
          {screen === 'result' && resultsData && (
            <motion.div 
              key="result" 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              className="w-full max-w-4xl mt-8"
            >
              
              <div className="text-center mb-10 sm:mb-14">
                <div className={`font-body inline-flex px-4 py-1.5 rounded-full border text-xs sm:text-sm font-bold tracking-wide mb-6 ${resultsData.badgeColor}`}>
                  {resultsData.badge}
                </div>
                {/* Puntuación gigante con font-heading */}
                <h2 className="font-heading text-6xl sm:text-7xl md:text-8xl font-extrabold tracking-tighter mb-4" style={{ color: resultsData.pct >= 75 ? '#34d399' : resultsData.pct >= 50 ? '#fbbf24' : '#e14141' }}>
                  {resultsData.pct}<span className="font-body text-3xl sm:text-4xl md:text-5xl text-zinc-600 font-medium tracking-normal">/100</span>
                </h2>
                <p className="font-body text-base sm:text-lg md:text-xl text-zinc-300 max-w-2xl mx-auto leading-relaxed font-medium px-4">{resultsData.desc}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-10 sm:mb-12">
                <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl sm:rounded-3xl p-6 sm:p-8">
                  <h3 className="font-body text-xs font-bold text-zinc-500 uppercase tracking-widest mb-6 sm:mb-8 border-b border-zinc-800/50 pb-4">Desglose por área</h3>
                  <div className="space-y-5 sm:space-y-6">
                    {resultsData.breakdown.map((b, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-xs sm:text-sm mb-2 font-medium font-body">
                          <span className="text-zinc-200">{b.name}</span>
                          <span className="text-zinc-400">{b.pct}%</span>
                        </div>
                        <div className="w-full h-1.5 sm:h-2 bg-zinc-950 rounded-full overflow-hidden border border-zinc-800/50">
                          <motion.div initial={{ width: 0 }} animate={{ width: `${b.pct}%` }} transition={{ duration: 0.8, delay: i * 0.1, ease: "easeOut" }} className="h-full rounded-full" style={{ backgroundColor: b.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl sm:rounded-3xl p-6 sm:p-8">
                  <h3 className="font-body text-xs font-bold text-zinc-500 uppercase tracking-widest mb-6 sm:mb-8 border-b border-zinc-800/50 pb-4">Puntos Críticos</h3>
                  <div className="space-y-4 sm:space-y-6">
                    {resultsData.weakAreas.map((w, i) => (
                      <div key={i} className="flex gap-3 sm:gap-4 items-start bg-zinc-950/50 p-4 rounded-xl sm:rounded-2xl border border-zinc-800/50">
                        <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: w.color }} />
                        <div>
                          <h4 className="font-body font-bold text-white mb-1 sm:mb-1.5 text-sm sm:text-base">{w.name}</h4>
                          <p className="font-body text-xs sm:text-sm text-zinc-400 leading-relaxed font-medium">{getInsight(w.area, w.pct)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="relative overflow-hidden bg-gradient-to-br from-[#e14141]/10 via-zinc-900/50 to-zinc-950 border border-[#e14141]/20 rounded-2xl sm:rounded-3xl p-8 sm:p-10 md:p-14 text-center">
                {/* CTA con font-heading */}
                <h3 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3 sm:mb-4 tracking-tight text-white">{resultsData.ctaTitle}</h3>
                <p className="font-body text-zinc-400 mb-6 sm:mb-8 max-w-2xl mx-auto text-sm sm:text-base md:text-lg font-medium">{resultsData.ctaBody}</p>
                
                {agendadoSuccess ? (
                  <div className="font-body inline-flex items-center justify-center gap-2 sm:gap-3 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-sm sm:text-lg">
                    <Check size={20} /> ¡Solicitud enviada con éxito!
                  </div>
                ) : (
                  <button 
                    onClick={handleAgendarLlamada}
                    disabled={isAgendando}
                    className="font-body inline-flex items-center justify-center gap-2 sm:gap-3 bg-white text-black px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-sm sm:text-lg hover:bg-zinc-200 transition-transform active:scale-95 disabled:opacity-70 disabled:cursor-wait"
                  >
                    {isAgendando ? 'Enviando solicitud...' : 'Agendar sesión de mejora'} 
                    {!isAgendando && <ArrowRight size={18} />}
                  </button>
                )}
              </div>

            </motion.div>
          )}

        </AnimatePresence>
      </main>
    </div>
  );
}
